import { useEffect } from 'react';
import toNumber from 'lodash/toNumber';

import { useCustomerAddress } from '../subscriptions/state/CustomerAddressContext';
import { useGlobalContext } from '../subscriptions/state/GlobalContext';
import { getCustomerAddress } from '../subscriptions/api';
import type { CustomerAddress } from '../subscriptions/state/CustomerAddressContext';

const transformLiquidToCustomerAddress = ({
  data,
  customerId,
}: {
  data: LiquidAddress[];
  customerId: string;
}): CustomerAddress[] => {
  return data.map((l) => {
    const { id, first_name, last_name, province_code, country_code, ...rest } =
      l;
    return {
      ...rest,
      id: id as string,
      customerId,
      firstName: first_name,
      lastName: last_name,
      provinceCode: province_code,
      countryCode: country_code,
    };
  });
};

export const transformApiResponseToCustomerAddress = ({
  data,
  customerId,
}: {
  data: CustomerAddressApiResponse;
  customerId: string;
}): CustomerAddress[] => {
  const { defaultAddress, addresses } = data;
  return addresses.map((a) => {
    const { countryCodeV2, ...rest } = a;
    return {
      ...rest,
      customerId,
      countryCode: countryCodeV2,
      default: a.id === defaultAddress.id,
    };
  });
};

export const useFetchCustomerAddress = (): void => {
  const { updateCustomerAddress } = useCustomerAddress();
  const { customerId, authToken } = useGlobalContext();

  useEffect(() => {
    const getAddress = async () => {
      if (!customerId) {
        return;
      }

      const hasLiquidAdress = window?.reactInit?.liquidAddresses;
      const addressesBelongToCustomer =
        hasLiquidAdress &&
        window?.reactInit?.liquidAddresses.every(
          (address) => toNumber(address.customer_id) === toNumber(customerId),
        );

      if (!addressesBelongToCustomer) {
        try {
          const apiResponse = await getCustomerAddress({
            customerId,
            authToken,
          });
          updateCustomerAddress(
            transformApiResponseToCustomerAddress({
              data: apiResponse,
              customerId,
            }),
          );
        } catch (e) {
          console.log('Error getting customer address', e);
        }
      } else {
        updateCustomerAddress(
          transformLiquidToCustomerAddress({
            data: window.reactInit.liquidAddresses,
            customerId,
          }),
        );
      }
    };

    getAddress();
  }, [updateCustomerAddress, customerId, authToken]);
};
